import {
  IonButton,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonText
} from "@ionic/react";
import { walletOutline } from "ionicons/icons";
import { useState } from "react";
import { currencyOptions } from "../constants";
import { applyMoneyInputText, getCurrencySymbol, parseMoneyInput } from "../logic/money";
import type { CurrencyOption, CurrencyPreference, PeriodType, SetupDefaults, SpendingConfiguration, ThemeMode } from "../types";
import { PotIndicator } from "./PotButton";
import { ThemeToggleButton } from "./ScreenActions";

interface SetupScreenProps {
  currencyPreference: CurrencyPreference;
  existingConfiguration: SpendingConfiguration | null;
  formatter: Intl.NumberFormat;
  maxMoneyAmount: number;
  potName: string | null;
  selectedCurrencyOption: CurrencyOption;
  setupDefaults: SetupDefaults;
  themeMode: ThemeMode;
  onCurrencyPreferenceChange: (preference: CurrencyPreference) => void;
  onCancel?: () => void;
  onOpenPotSelector?: () => void;
  onStartTracking: (periodAllowance: number, periodType: PeriodType) => void;
  onToggleTheme: () => void;
}

export function SetupScreen({
  currencyPreference,
  existingConfiguration,
  formatter,
  maxMoneyAmount,
  potName,
  selectedCurrencyOption,
  setupDefaults,
  themeMode,
  onCurrencyPreferenceChange,
  onCancel,
  onOpenPotSelector,
  onStartTracking,
  onToggleTheme
}: SetupScreenProps) {
  const [allowanceText, setAllowanceText] = useState(
    (existingConfiguration?.periodAllowance ?? setupDefaults.periodAllowance)?.toString() ?? ""
  );
  const [periodType, setPeriodType] = useState<PeriodType>(
    existingConfiguration?.periodType ?? setupDefaults.periodType
  );
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const currencySymbol = getCurrencySymbol(formatter);

  function submitSetup() {
    const allowance = parseMoneyInput(allowanceText);

    if (!allowance || allowance <= 0 || allowance > maxMoneyAmount) {
      setValidationMessage(`Enter a valid allowance amount up to ${formatter.format(maxMoneyAmount)}.`);
      return;
    }

    setValidationMessage(null);
    onStartTracking(allowance, periodType);
  }

  return (
    <main className="tracker-screen setup-screen">
      <section className="setup-hero">
        <IonIcon icon={walletOutline} aria-hidden="true" />
        <h1>Keep today on track</h1>
        <p>Set an allowance and Pyeah will recalculate what is left for each day.</p>
      </section>

      <section className="control-panel">
        {onOpenPotSelector ? (
          <PotIndicator potName={potName} onOpenPotSelector={onOpenPotSelector} />
        ) : null}

        <div className="setup-preferences" aria-label="SpendTrack preferences">
          <div className="setup-preference-row setup-currency-setting">
            <span>Currency</span>
            <IonSelect
              aria-label="Currency"
              interface="popover"
              value={`${currencyPreference.locale}|${currencyPreference.currency}`}
              selectedText={`${selectedCurrencyOption.flag} ${selectedCurrencyOption.shortLabel}`}
              onIonChange={(event) => {
                const [locale, currency] = String(event.detail.value).split("|");
                onCurrencyPreferenceChange({ locale, currency });
              }}
            >
              {currencyOptions.map((option) => (
                <IonSelectOption value={`${option.locale}|${option.currency}`} key={`${option.locale}-${option.currency}`}>
                  {option.flag} {option.label}
                </IonSelectOption>
              ))}
            </IonSelect>
          </div>
          <div className="setup-preference-row">
            <span>Theme</span>
            <ThemeToggleButton themeMode={themeMode} onToggleTheme={onToggleTheme} />
          </div>
        </div>

        <IonItem lines="none" className="field-item money-field">
          <IonLabel position="stacked">Allowance</IonLabel>
          <span className="currency-prefix" aria-hidden="true">
            {currencySymbol}
          </span>
          <IonInput
            inputmode="decimal"
            max={maxMoneyAmount}
            placeholder="Enter allowance"
            value={allowanceText}
            onIonInput={(event) => applyMoneyInputText(event, setAllowanceText, maxMoneyAmount)}
          />
        </IonItem>

        <div className="segment-wrap" aria-label="Tracking period">
          <IonSegment value={periodType} onIonChange={(event) => setPeriodType(event.detail.value as PeriodType)}>
            <IonSegmentButton value="weekly">Weekly</IonSegmentButton>
            <IonSegmentButton value="monthly">Monthly</IonSegmentButton>
          </IonSegment>
        </div>

        {validationMessage ? (
          <IonText color="danger">
            <p className="validation-message">{validationMessage}</p>
          </IonText>
        ) : null}

        <div className="setup-actions">
          {onCancel ? (
            <IonButton expand="block" fill="outline" size="large" onClick={onCancel}>
              Return to Budget
            </IonButton>
          ) : null}
          <IonButton expand="block" size="large" onClick={submitSetup}>
            Start Tracking
          </IonButton>
        </div>
      </section>
    </main>
  );
}
